"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ShieldCheck, 
  Cookie, 
  Info, 
  Check, 
  Settings, 
  ArrowLeft, 
  HelpCircle, 
  Eye, 
  EyeOff, 
  FileText,
  Calendar,
  Lock,
  Activity,
  Award
} from "lucide-react";
import Link from "next/link";
import { useCookieConsent } from "@/components/layout/CookieConsentProvider";

export default function CookiesPolicyPage() {
  const { consent, saveCustomConsent, acceptAll, rejectOptional } = useCookieConsent();
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>("essential");

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (consent) {
      setAnalytics(consent.analytics);
      setMarketing(consent.marketing);
    }
  }, [consent]);

  const handleSaveCustom = async () => {
    await saveCustomConsent(analytics, marketing);
    setSavedSuccess(true);
    setTimeout(() => {
      setSavedSuccess(false);
    }, 3000);
  };

  const handleAcceptAll = async () => {
    await acceptAll();
    setAnalytics(true);
    setMarketing(true);
    setSavedSuccess(true);
    setTimeout(() => {
      setSavedSuccess(false);
    }, 3000);
  };

  const handleDeclineAll = async () => {
    await rejectOptional();
    setAnalytics(false);
    setMarketing(false);
    setSavedSuccess(true);
    setTimeout(() => {
      setSavedSuccess(false);
    }, 3000);
  };

  if (!mounted) return null;

  const cookieInventory = {
    essential: [
      { name: "fxr_session", provider: "FIXER (1st party)", purpose: "Safeguards your active authenticated login state across user and contractor dashboards.", lifespan: "30 Days", type: "Secure Cookie" },
      { name: "fxr_cookie_consent", provider: "FIXER (1st party)", purpose: "Saves and respects your dynamic cookie selection preferences recorded on this panel.", lifespan: "1 Year", type: "Local Storage" },
      { name: "csrf_token", provider: "FIXER (1st party)", purpose: "Prevents Cross-Site Request Forgery security vulnerability threats during critical form submissions.", lifespan: "Session", type: "HTTP Only Secure" },
      { name: "__stripe_mid / __stripe_sid", provider: "Stripe (3rd party)", purpose: "Essential token metrics used by Stripe to analyze potential payment fraud during checkout.", lifespan: "1 Year / 30 Min", type: "Third Party Secure" }
    ],
    analytics: [
      { name: "_ga", provider: "Google Analytics", purpose: "Compiles completely anonymous visitor telemetry data, focusing on load times and site usage.", lifespan: "2 Years", type: "HTTP Cookie" },
      { name: "_gid", provider: "Google Analytics", purpose: "Records and analyzes session progression patterns through the trade classification wizard.", lifespan: "24 Hours", type: "HTTP Cookie" },
      { name: "fxr_telemetry_engine", provider: "FIXER (1st party)", purpose: "Collects execution analytics for the AI-powered diagnostic pricing estimation algorithms.", lifespan: "7 Days", type: "LocalStorage" }
    ],
    marketing: [
      { name: "_fbp", provider: "Meta/Facebook", purpose: "Optimizes contractor registration efficiency metrics and acquisition performance reporting.", lifespan: "3 Months", type: "Pixel Cookie" },
      { name: "ads/ga-audiences", provider: "Google Ads", purpose: "Safely delivers targeted service reminders to homeowners who leave a booking description partially filled.", lifespan: "Session", type: "Web Pixels" }
    ]
  };

  return (
    <main className="min-h-screen bg-slate-50 pb-24 pt-32 text-slate-900 relative overflow-hidden">
      {/* Visual Ambient Decorative Accents */}
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-blue-500/5 rounded-full blur-[140px] pointer-events-none" />

      <div className="mx-auto max-w-6xl px-6 relative z-10">
        
        {/* Header Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold uppercase tracking-widest mb-6 border border-emerald-100 shadow-sm shadow-emerald-100/40">
            <Cookie size={14} className="text-emerald-600 animate-spin-slow" />
            <span>Data Transparency System</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black tracking-tight text-slate-950 mb-6 uppercase italic">
            Privacy & Cookie Center
          </h1>
          <p className="text-base md:text-lg text-slate-500 font-medium max-w-3xl mx-auto leading-relaxed">
            Configure exactly how our application manages telemetry cookies. At FIXER, we prioritize digital privacy, absolute transparency, and state-of-the-art security.
          </p>
        </motion.div>

        {/* Dynamic Dual-Column Workspace */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start mb-20">
          
          {/* Detailed Auditing Section (Left Col) */}
          <div className="lg:col-span-7 space-y-6">
            
            {/* Overview Card */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white p-8 md:p-10 rounded-[2.5rem] border border-slate-200/80 shadow-md shadow-slate-100/50"
            >
              <h2 className="text-xl md:text-2xl font-black text-slate-950 mb-4 flex items-center gap-2 italic uppercase">
                <FileText size={20} className="text-emerald-600" />
                1. Transparency Principles
              </h2>
              <p className="text-slate-500 text-sm font-medium leading-relaxed mb-4">
                We write lightweight secure keys (commonly known as cookies or web tokens) to facilitate seamless, secure sessions.
              </p>
              <p className="text-slate-500 text-sm font-medium leading-relaxed">
                By segregating cookies into independent layers, you can customize your footprint dynamically. Rejecting optional tracking has absolutely no impact on core features like chat, image uploading, or real-time GPS dispatching.
              </p>
            </motion.div>

            {/* Granular Inventory Listing */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white p-8 md:p-10 rounded-[2.5rem] border border-slate-200/80 shadow-md shadow-slate-100/50"
            >
              <h2 className="text-xl md:text-2xl font-black text-slate-950 mb-6 flex items-center gap-2 italic uppercase">
                <Award size={20} className="text-emerald-600" />
                2. Granular Cookie Inventory
              </h2>
              
              <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-6">
                Click any category header below to inspect the exact parameters of actively registered cookie trackers:
              </p>

              {/* Accordion Categories */}
              <div className="space-y-4">
                
                {/* 1. Necessary */}
                <div className="border border-slate-200 rounded-2xl overflow-hidden">
                  <button
                    onClick={() => setExpandedSection(expandedSection === "essential" ? null : "essential")}
                    className="w-full px-6 py-4.5 bg-slate-50 hover:bg-slate-100/80 flex items-center justify-between transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Lock size={16} className="text-slate-500" />
                      <span className="font-bold text-sm text-slate-900">Strictly Necessary Cookies</span>
                    </div>
                    <span className="text-[10px] font-black tracking-widest text-slate-400 uppercase">
                      {expandedSection === "essential" ? "Collapse" : "Expand"}
                    </span>
                  </button>
                  
                  <AnimatePresence initial={false}>
                    {expandedSection === "essential" && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="border-t border-slate-200 overflow-hidden"
                      >
                        <div className="p-6 bg-white overflow-x-auto">
                          <table className="w-full text-left text-xs min-w-[500px]">
                            <thead>
                              <tr className="border-b border-slate-100 text-slate-400 font-bold uppercase tracking-wider">
                                <th className="pb-3">Key Name</th>
                                <th className="pb-3">Provider</th>
                                <th className="pb-3">Retention</th>
                                <th className="pb-3">Role</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 text-slate-500">
                              {cookieInventory.essential.map((cookie, idx) => (
                                <tr key={idx} className="hover:bg-slate-50/50">
                                  <td className="py-3 font-mono font-bold text-slate-800">{cookie.name}</td>
                                  <td className="py-3">{cookie.provider}</td>
                                  <td className="py-3">{cookie.lifespan}</td>
                                  <td className="py-3 text-[11px] leading-relaxed max-w-[200px]">{cookie.purpose}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* 2. Analytics */}
                <div className="border border-slate-200 rounded-2xl overflow-hidden">
                  <button
                    onClick={() => setExpandedSection(expandedSection === "analytics" ? null : "analytics")}
                    className="w-full px-6 py-4.5 bg-slate-50 hover:bg-slate-100/80 flex items-center justify-between transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Activity size={16} className="text-emerald-500 animate-pulse" />
                      <span className="font-bold text-sm text-slate-900">Performance & Analytics Cookies</span>
                    </div>
                    <span className="text-[10px] font-black tracking-widest text-slate-400 uppercase">
                      {expandedSection === "analytics" ? "Collapse" : "Expand"}
                    </span>
                  </button>

                  <AnimatePresence initial={false}>
                    {expandedSection === "analytics" && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="border-t border-slate-200 overflow-hidden"
                      >
                        <div className="p-6 bg-white overflow-x-auto">
                          <table className="w-full text-left text-xs min-w-[500px]">
                            <thead>
                              <tr className="border-b border-slate-100 text-slate-400 font-bold uppercase tracking-wider">
                                <th className="pb-3">Key Name</th>
                                <th className="pb-3">Provider</th>
                                <th className="pb-3">Retention</th>
                                <th className="pb-3">Role</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 text-slate-500">
                              {cookieInventory.analytics.map((cookie, idx) => (
                                <tr key={idx} className="hover:bg-slate-50/50">
                                  <td className="py-3 font-mono font-bold text-slate-800">{cookie.name}</td>
                                  <td className="py-3">{cookie.provider}</td>
                                  <td className="py-3">{cookie.lifespan}</td>
                                  <td className="py-3 text-[11px] leading-relaxed max-w-[200px]">{cookie.purpose}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* 3. Marketing */}
                <div className="border border-slate-200 rounded-2xl overflow-hidden">
                  <button
                    onClick={() => setExpandedSection(expandedSection === "marketing" ? null : "marketing")}
                    className="w-full px-6 py-4.5 bg-slate-50 hover:bg-slate-100/80 flex items-center justify-between transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <HelpCircle size={16} className="text-blue-500" />
                      <span className="font-bold text-sm text-slate-900">Marketing & Acquisition Cookies</span>
                    </div>
                    <span className="text-[10px] font-black tracking-widest text-slate-400 uppercase">
                      {expandedSection === "marketing" ? "Collapse" : "Expand"}
                    </span>
                  </button>

                  <AnimatePresence initial={false}>
                    {expandedSection === "marketing" && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="border-t border-slate-200 overflow-hidden"
                      >
                        <div className="p-6 bg-white overflow-x-auto">
                          <table className="w-full text-left text-xs min-w-[500px]">
                            <thead>
                              <tr className="border-b border-slate-100 text-slate-400 font-bold uppercase tracking-wider">
                                <th className="pb-3">Key Name</th>
                                <th className="pb-3">Provider</th>
                                <th className="pb-3">Retention</th>
                                <th className="pb-3">Role</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 text-slate-500">
                              {cookieInventory.marketing.map((cookie, idx) => (
                                <tr key={idx} className="hover:bg-slate-50/50">
                                  <td className="py-3 font-mono font-bold text-slate-800">{cookie.name}</td>
                                  <td className="py-3">{cookie.provider}</td>
                                  <td className="py-3">{cookie.lifespan}</td>
                                  <td className="py-3 text-[11px] leading-relaxed max-w-[200px]">{cookie.purpose}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

              </div>

            </motion.div>

          </div>

          {/* Preferences Settings Form panel (Right Col - Sticky) */}
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white p-8 md:p-10 rounded-[3rem] border border-slate-200 shadow-xl relative overflow-hidden"
            >
              {/* Dynamic subtle setting gears watermarked in background */}
              <div className="absolute top-0 right-0 p-8 opacity-[0.02] pointer-events-none">
                <Settings size={150} />
              </div>

              <div className="mb-8 relative z-10">
                <h3 className="text-2xl font-black text-slate-950 italic uppercase">Preference Center</h3>
                <p className="text-slate-400 text-xs font-semibold mt-2 italic">
                  Take absolute control over your browser preferences. Changes take effect instantly and are synced to your user session securely.
                </p>
              </div>

              {/* Custom Selector Panel */}
              <div className="space-y-5 mb-8 relative z-10">
                
                {/* 1. Necessary (Disabled Toggle) */}
                <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200/60 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400">
                      <Lock size={16} />
                    </div>
                    <div>
                      <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider">Necessary</h4>
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">Always Enabled</p>
                    </div>
                  </div>
                  <span className="text-[9px] px-2.5 py-1 rounded-full bg-slate-200 text-slate-500 font-black tracking-widest uppercase border border-slate-300/40">
                    Strict
                  </span>
                </div>

                {/* 2. Performance & Analytics Toggle */}
                <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200/60 space-y-3.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600">
                        <Activity size={16} />
                      </div>
                      <div>
                        <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider">Performance</h4>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">Analytics Stats</p>
                      </div>
                    </div>
                    <span className={`text-[8px] px-2 py-0.5 rounded-full font-black tracking-widest uppercase border ${
                      analytics 
                        ? "bg-emerald-50 border-emerald-200 text-emerald-700" 
                        : "bg-slate-200 border-slate-300 text-slate-500"
                    }`}>
                      {analytics ? "Active" : "Disabled"}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setAnalytics(true)}
                      className={`py-2 rounded-xl text-[9px] font-black uppercase tracking-wider border transition-all active:scale-[0.98] ${
                        analytics 
                          ? "bg-emerald-600 border-emerald-700 text-white shadow-md shadow-emerald-600/10" 
                          : "bg-white border-slate-200 text-slate-500 hover:bg-slate-100/50"
                      }`}
                    >
                      Accept
                    </button>
                    <button
                      onClick={() => setAnalytics(false)}
                      className={`py-2 rounded-xl text-[9px] font-black uppercase tracking-wider border transition-all active:scale-[0.98] ${
                        !analytics 
                          ? "bg-slate-800 border-slate-900 text-white shadow-md shadow-slate-800/10" 
                          : "bg-white border-slate-200 text-slate-500 hover:bg-slate-100/50"
                      }`}
                    >
                      Decline
                    </button>
                  </div>
                </div>

                {/* 3. Marketing Toggle */}
                <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200/60 space-y-3.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600">
                        <HelpCircle size={16} />
                      </div>
                      <div>
                        <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider">Marketing</h4>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">Campaign Tracking</p>
                      </div>
                    </div>
                    <span className={`text-[8px] px-2 py-0.5 rounded-full font-black tracking-widest uppercase border ${
                      marketing 
                        ? "bg-emerald-50 border-emerald-200 text-emerald-700" 
                        : "bg-slate-200 border-slate-300 text-slate-500"
                    }`}>
                      {marketing ? "Active" : "Disabled"}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setMarketing(true)}
                      className={`py-2 rounded-xl text-[9px] font-black uppercase tracking-wider border transition-all active:scale-[0.98] ${
                        marketing 
                          ? "bg-emerald-600 border-emerald-700 text-white shadow-md shadow-emerald-600/10" 
                          : "bg-white border-slate-200 text-slate-500 hover:bg-slate-100/50"
                      }`}
                    >
                      Accept
                    </button>
                    <button
                      onClick={() => setMarketing(false)}
                      className={`py-2 rounded-xl text-[9px] font-black uppercase tracking-wider border transition-all active:scale-[0.98] ${
                        !marketing 
                          ? "bg-slate-800 border-slate-900 text-white shadow-md shadow-slate-800/10" 
                          : "bg-white border-slate-200 text-slate-500 hover:bg-slate-100/50"
                      }`}
                    >
                      Decline
                    </button>
                  </div>
                </div>

              </div>

              {/* Action buttons */}
              <div className="flex flex-col gap-3 relative z-10">
                <button
                  onClick={handleSaveCustom}
                  className="w-full py-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black uppercase tracking-wider transition-all shadow-lg shadow-emerald-600/20 active:scale-[0.99] flex items-center justify-center gap-2"
                >
                  <ShieldCheck size={14} />
                  Save Custom Preferences
                </button>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={handleAcceptAll}
                    className="py-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-[10px] font-black uppercase tracking-wider transition-all active:scale-[0.99]"
                  >
                    Accept All
                  </button>
                  <button
                    onClick={handleDeclineAll}
                    className="py-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-[10px] font-black uppercase tracking-wider transition-all active:scale-[0.99]"
                  >
                    Reject Optional
                  </button>
                </div>
              </div>

              {/* Success Notification Alert */}
              <AnimatePresence>
                {savedSuccess && (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    className="mt-4 p-4.5 rounded-xl bg-emerald-50 text-emerald-800 border border-emerald-100 text-xs font-black uppercase tracking-widest text-center flex items-center justify-center gap-2"
                  >
                    <Check size={14} className="text-emerald-600" />
                    <span>Updated Successfully!</span>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Audit Metadata panel */}
              <div className="mt-8 pt-6 border-t border-slate-100 text-[10px] font-bold text-slate-400 space-y-1.5 uppercase tracking-wider relative z-10">
                <div className="flex justify-between items-center">
                  <span>Consent Status:</span>
                  <span className="text-emerald-600 font-extrabold flex items-center gap-1">
                    <Check size={10} />
                    Verified Choice
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Last Modified:</span>
                  <span className="font-mono text-slate-500">
                    {consent?.updatedAt ? new Date(consent.updatedAt).toLocaleDateString() : "Just now"}
                  </span>
                </div>
              </div>

            </motion.div>
          </div>

        </div>

        {/* Extra resources links */}
        <div className="mt-16 pt-12 border-t border-slate-200/80 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex gap-6 text-xs font-black uppercase tracking-widest text-slate-400">
            <Link href="/privacy" className="hover:text-emerald-600 transition-colors">Privacy Policy</Link>
            <Link href="/terms" className="hover:text-emerald-600 transition-colors">Terms of Service</Link>
          </div>
          <p className="text-xs font-semibold text-slate-400">
            Last Audited Revisions: April 24, 2026
          </p>
        </div>

        <div className="mt-16 text-center">
          <Link href="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-emerald-600 transition-colors text-xs font-black uppercase tracking-widest">
            <ArrowLeft size={14} />
            Back to home
          </Link>
        </div>
      </div>
    </main>
  );
}
